import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Ban } from 'lucide-react';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

import { darDeBaja } from '@/services/api/inventario.api';
import { getApiErrorMessage } from '@/services/api/client';

export interface DarDeBajaItem {
  id_item_maestro: string;
  serial_global: string;
}

interface Props {
  item: DarDeBajaItem | null;
  onOpenChange: (open: boolean) => void;
}

export function DarDeBajaDialog({ item, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [observaciones, setObservaciones] = useState('');

  const mutation = useMutation({
    mutationFn: () => darDeBaja(item!.id_item_maestro, { observaciones: observaciones.trim() || undefined }),
    onSuccess: (data) => {
      toast.success(`${data.serial_global} dado de baja`, {
        description: data.empleado_desasignado ? 'El empleado asignado fue desvinculado.' : undefined,
      });
      qc.invalidateQueries({ queryKey: ['inventario-items'] });
      onOpenChange(false);
    },
    onError: (err) => {
      toast.error('No se pudo dar de baja', { description: getApiErrorMessage(err) });
    },
  });

  return (
    <Dialog open={!!item} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-red-50 text-red-600">
              <Ban className="h-4 w-4" />
            </span>
            Dar de baja
          </DialogTitle>
          <DialogDescription>
            Esta acción es irreversible. <span className="font-medium text-foreground">{item?.serial_global}</span> quedará fuera de servicio.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-1.5">
          <Label htmlFor="obs-baja">Motivo <span className="text-muted-foreground">(opcional)</span></Label>
          <Textarea
            id="obs-baja"
            placeholder="Ej: Daño irreparable, obsolescencia..."
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            rows={3}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button variant="destructive" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? <><Loader2 className="animate-spin" />Procesando...</> : 'Confirmar baja'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
